import classNames from "classnames";
import { debounce, isFunction } from "lodash-es";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Async from "react-select/async";
import AsyncCreatable from "react-select/async-creatable";

import { testAttr } from "@/browser";

import { Icon } from "../../../Icon";
import { ITypeaheadProps, SelectComponents, SELECT_CLASS_NAME } from "./components";

const {
  ClearIndicator,
  DropdownIndicator,
  Input,
  LoadingIndicator,
  AutoPositionedMenu,
  MenuList,
  MultiValue,
  MultiValueRemove,
  Option,
  SingleValue,
} = SelectComponents;

export const Typeahead = memo(<T,>(props: ITypeaheadProps<T>) => {
  const {
    autoload,
    className,
    components,
    debounceTime = 500, // not used by react-select
    hideSelectedOptions = true,
    icons = {
      clear: <Icon icon="close" size="sm" />,
      dropdownMenuOpen: <Icon icon="arrow_up_2" size="sm" />,
      dropdownMenuClosed: <Icon icon="arrow_down_2" size="sm" />,
    }, // not used by react-select
    invalid,
    isCreatable = false,
    isDisabled,
    labelKey = "name", // not used by react-select
    loadOptions,
    onChange,
    placeholder = "Type...",
    readonly,
    size = "md",
    valueKey = "id", // not used by react-select,
    testName,
    ...rest
  } = props;

  const { t } = useTranslation("common");

  const proxyLoad: ITypeaheadProps<T>["loadOptions"] = useCallback(
    (input, callback) => {
      if (autoload || input) {
        loadOptions(input, callback);
      }
    },
    [loadOptions],
  );
  const debouncedFetch = useMemo(() => debounce(proxyLoad, debounceTime), [proxyLoad, debounceTime]);

  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, []);

  const getOptionLabel = useCallback(
    (option: any) => {
      if (isFunction(labelKey)) {
        return labelKey(option);
      }
      return option[labelKey as string];
    },
    [labelKey],
  );

  const getOptionValue = useCallback(
    (option: any) => {
      return option[valueKey];
    },
    [valueKey],
  );

  const handleChange = useCallback(
    (value: T | null, _actionMeta: any) => {
      if (onChange) {
        onChange(value);
      }
    },
    [onChange],
  );

  const wrapperClasses = classNames(className, `${SELECT_CLASS_NAME}-wrapper`);
  const classes = classNames(className, SELECT_CLASS_NAME, {
    [`${SELECT_CLASS_NAME}--size-${size}`]: !!size,
    [`${SELECT_CLASS_NAME}--not-valid`]: invalid,
  });
  const TypeaheadComponent = isCreatable ? AsyncCreatable : Async;

  return (
    <span className={wrapperClasses} {...testAttr({ testName }, { sufix: "typeahead" })}>
      <TypeaheadComponent
        className={classes}
        classNamePrefix={SELECT_CLASS_NAME}
        components={{
          ClearIndicator,
          DropdownIndicator,
          IndicatorSeparator: () => null,
          Input,
          LoadingIndicator,
          Menu: AutoPositionedMenu,
          MenuList,
          MultiValue,
          MultiValueRemove,
          Option,
          SingleValue,
          ...components,
        }}
        defaultOptions={autoload}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        hideSelectedOptions={hideSelectedOptions}
        icons={icons}
        isDisabled={isDisabled || readonly}
        loadOptions={debouncedFetch}
        onChange={handleChange}
        placeholder={placeholder}
        size={size}
        invalid={invalid}
        loadingMessage={() => t("COMPONENTS.TYPEAHEAD.LOADING")}
        {...rest}
      />
    </span>
  );
});

Typeahead.displayName = "Typeahead";
